import { Popover, Table, Typography } from "antd";
import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import { ApiNames } from "../../api/api";
import { SUPPORTED_COVERAGES } from "../../constant/patient";
import { PAGE_SIZE, selectedViews } from "../../constant/table";
import { TabsMap } from "../../screen/patient/Patient";
import ACTION from "../../store/action";
import { selectIsFetching, selectTableColumns, selectVisitScheduleData } from "../../store/selector";
import useTableInlineEdit from "../../hooks/InlineEdit/useTableInlineEdit";
import { DATE_TIME_FORMAT, dateFormatter, dateTimeFormatter, utcDateFormatter } from "../../utils/date";
import { fetchTableData } from "../../utils/fetchTableData";
import { ColumnSelector } from "../ColumnSelector";
import { patientInsuranceCell } from "../TableCells";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import LinkableCell from "../UI/LinkableCell";
import { EditColumn, PatientNameColumn, StarColumn, withSorting } from "./CommonColumns";
import ConsentStatusColumn from "./ConsentStatusColumn";
import EmptyTableMessage from "./EmptyTableMessage";
import PatientPageCount from "./PatientPageCount";
import { ColumnSelectorContainer } from "./TableStyle";
import { commonBulkActionsTableStyle } from "./BulkActionsTableStyle";
import useColumnResizeAndReorder from "../../hooks/useColumnResizeAndReorder";
import { TableColumnWrapper } from "@tailormed/common-client/component/ColumnResizeAndReorder/TableColumnWrapper";
import { ColumnsKeys } from "../../constant/columns";

const StyledTable = styled(Table)`
  ${(props) => commonBulkActionsTableStyle(props.isFilterListView, props.$hasSelection)}
`;
const { Text } = Typography;

const PAGE_COUNT_TITLE_KEY = "visits";

const patientPath = ({ patientId, journeyId }) => TabsMap.overview.path(patientId, journeyId);

const renderWithLink = (patientRec, renderFn) => <LinkableCell to={patientPath(patientRec)}>{renderFn()}</LinkableCell>;

const VisitScheduleTableComponent = ({
  tableColumns,
  isFetching,
  setTableDataAct,
  tableData,
  t,
  updateSavedFilterViewParameters,
  isFilterListView,
  rowSelection
}) => {
  const [hoveringLineIndex, setHoveringLineIndex] = useState(null);
  const { processColumns, components, containerRef, reorderGuideLeft, tableProps } = useColumnResizeAndReorder(
    ColumnsKeys.tables["visit-schedule"],
    ["star", "edit"]
  );

  const totalRecordsCount = tableData?.totalPatients;
  const sortingEnabled = tableData?.sortingEnabled;

  const { renderPatientCell } = useTableInlineEdit({
    t,
    view: selectedViews.visitSchedule,
    renderWithLink
  });

  // Rows are keyed per visit (visitScheduleId), but bulk selection operates on patients.
  // Translate the patient-level selection coming from BulkActionsContainer into the
  // visit-row keys Ant Table renders, and map row toggles back to patient ids.
  const visitRowSelection = useMemo(() => {
    if (!rowSelection) return rowSelection;
    const patients = tableData?.patients || [];
    const selectedPatientIds = new Set(rowSelection.selectedRowKeys || []);
    return {
      ...rowSelection,
      selectedRowKeys: patients
        .filter((patient) => selectedPatientIds.has(patient.patientId))
        .map((patient) => patient.visitScheduleId),
      onChange: (selectedVisitKeys) => {
        const selectedVisitKeySet = new Set(selectedVisitKeys);
        const selectedPatients = [
          ...new Set(
            patients.filter((patient) => selectedVisitKeySet.has(patient.visitScheduleId)).map((p) => p.patientId)
          )
        ];
        rowSelection.onChange?.(selectedPatients);
      }
    };
  }, [rowSelection, tableData?.patients]);

  const columns = [
    {
      dataIndex: tableColumns[selectedViews.visitSchedule]?.star?.dataIndex,
      key: tableColumns[selectedViews.visitSchedule]?.star?.key,
      width: 32,
      className: "star-column",
      render: (text, record) => <StarColumn record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.name_id, {
      minWidth: 300,
      sorterTooltipText: t("columns.sortById"),
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn record={record} />)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allPatients]?.isDeceased, {
      render: (_, record) => renderPatientCell("isDeceased", record)
    }),
    {
      ...tableColumns[selectedViews.visitSchedule]?.phone_number,
      render: (text, record) => renderPatientCell("phone_number", record)
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.dob,
      render: (text, record) => renderPatientCell("dob", record)
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.ssn,
      render: (text, record) => renderPatientCell("ssn", record)
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.primaryDiagnosis,
      render: (text, record) => renderWithLink(record, () => (record.diagnosises[0] ? record.diagnosises[0] : null))
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.assignee,
      width: 230,
      render: (text, record) => renderPatientCell("assignee", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.visitFacility, {
      render: (_, record) => renderWithLink(record, () => record.visitFacility)
    }),
    {
      ...tableColumns[selectedViews.visitSchedule]?.facility,
      sorter: false,
      render: (_, record) => renderPatientCell("facility", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.physician, {
      render: (text, record) => renderPatientCell("physician", record)
    }),
    {
      ...tableColumns[selectedViews.visitSchedule]?.employer,
      render: (_, record) => renderWithLink(record, () => record.employer)
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.adminDate,
      render: (text, record) =>
        renderWithLink(record, () =>
          record.adminDates ? (
            <Popover
              overlayInnerStyle={{ padding: "20px" }}
              content={
                <div>
                  <Text>{t("popover_visit_schedule")}</Text>
                  <ul style={{ marginTop: "20px" }}>
                    {record.adminDates.map((itemDates, keyDates) => (
                      <li key={keyDates}>
                        <Text style={{ fontWeight: "bold" }}>{dateTimeFormatter(itemDates)}</Text>
                        {record.adminTypes &&
                          record.adminTypes.map(
                            (itemTypes, keyTypes) => keyDates === keyTypes && <Text> - {itemTypes}</Text>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              }
            >
              <p
                style={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  width: "170px",
                  overflow: "hidden"
                }}
              >
                {record.adminDates.map((item, key) =>
                  key + 1 === record.adminDates.length ? dateTimeFormatter(item) : dateTimeFormatter(item) + " & "
                )}
              </p>
            </Popover>
          ) : (
            utcDateFormatter(record.adminDate, DATE_TIME_FORMAT)
          )
        )
    },

    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.adminType, {}),

    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.creationDate, {
      render: (text, record) => renderWithLink(record, () => dateTimeFormatter(record.creationDate, "MMM D, YYYY"))
    }),
    {
      ...tableColumns[selectedViews.visitSchedule]?.agreementSignatureDate,
      render: (text, record) =>
        renderWithLink(record, () => record.activeConsentDate && dateFormatter(record.activeConsentDate))
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.consentStatus,
      render: (text, record) => renderWithLink(record, () => <ConsentStatusColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.patientStatus, {
      render: (_, record) => renderPatientCell("patientStatus", record)
    }),
    {
      ...tableColumns[selectedViews.visitSchedule]?.primaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.primary)(text, record))
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.secondaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.secondary)(text, record))
    },
    {
      ...tableColumns[selectedViews.visitSchedule]?.tertiaryInsurancePayer,
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.tertiary)(text, record))
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.visitSchedule]?.followupDate, {
      render: (text, record) => renderPatientCell("followupDate", record)
    }),
    {
      dataIndex: tableColumns[selectedViews.visitSchedule]?.edit?.dataIndex,
      key: tableColumns[selectedViews.visitSchedule]?.edit?.key,
      render: (_, record, rowIndex) => <EditColumn isHovering={hoveringLineIndex === rowIndex} record={record} />
    }
  ];

  return (
    <>
      {!!tableData?.patients.length && (
        <ColumnSelectorContainer>
          <ColumnSelector />
        </ColumnSelectorContainer>
      )}
      <TableColumnWrapper containerRef={containerRef} reorderGuideLeft={reorderGuideLeft}>
        <StyledTable
          {...tableProps}
          size="small"
          rowClassName="cursor-pointer"
          style={{ width: "100%" }}
          loading={{ indicator: <TailormedSpinner />, spinning: isFetching }}
          components={components}
          columns={processColumns(
            columns.filter((col) => tableColumns[selectedViews.visitSchedule]?.[col.key]?.display)
          )}
          locale={{
            emptyText: <EmptyTableMessage />
          }}
          onRow={(_, rowIndex) => {
            return {
              onMouseEnter: () => setHoveringLineIndex(rowIndex),
              onMouseLeave: () => setHoveringLineIndex(null)
            };
          }}
          onChange={(pagination, filters, sorter) =>
            fetchTableData(pagination, sorter, tableData, setTableDataAct, updateSavedFilterViewParameters)
          }
          isFilterListView={isFilterListView}
          rowKey="visitScheduleId"
          rowSelection={visitRowSelection}
          $hasSelection={rowSelection?.selectedRowKeys?.length > 0}
          pagination={{
            size: "small",
            showSizeChanger: false,
            current: tableData && tableData.selectedPage,
            defaultPageSize: PAGE_SIZE,
            pageSize: PAGE_SIZE,
            position: ["bottom"],
            total: totalRecordsCount,
            showTotal: (total, range) => (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <PatientPageCount
                  total={total}
                  range={range}
                  isAccurateCount={tableData.isAccurateCount}
                  titleKey={PAGE_COUNT_TITLE_KEY}
                />
              </div>
            )
          }}
          dataSource={
            tableData && tableData.patients
              ? tableData.patients
                  .map((patient, index) => ({ ...patient, key: index }))
                  .sort((a, b) => b.isActive - a.isActive)
              : null
          }
        />
      </TableColumnWrapper>
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  tableColumns: selectTableColumns,
  isFetching: selectIsFetching([ApiNames.VisitSchedulePhiSearch, ApiNames.VisitScheduleSearch]),
  tableData: selectVisitScheduleData
});

const mapDispatchToProps = (dispatch) => ({
  setTableDataAct: (page, sorter) => dispatch(ACTION.paginateSortChange(page, selectedViews.visitSchedule, sorter))
});

const VisitScheduleTable = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(VisitScheduleTableComponent);

export { VisitScheduleTable };
